
import { createContext, useContext, useState, ReactNode, useEffect } from "react";

type WizardContextType = {
  currentStep: number;
  nextStep: () => void;
  previousStep: () => void;
  goToStep: (step: number) => void;
  totalSteps: number;
  isLastStep: boolean;
  isReviewStep: boolean;
  setDynamicTotalSteps: (steps: number) => void;
  triggerStepValidation: boolean;
  setTriggerStepValidation: (value: boolean) => void;
  setCurrentStepDirect: (step: number) => void;
};

const WizardContext = createContext<WizardContextType | undefined>(undefined);

export const WizardProvider = ({ 
  children, 
  totalSteps: initialTotalSteps 
}: { 
  children: ReactNode; 
  totalSteps: number 
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [totalSteps, setTotalSteps] = useState(initialTotalSteps);
  const [triggerStepValidation, setTriggerStepValidation] = useState(false);

  // Reset current step when total steps change
  useEffect(() => {
    if (currentStep > totalSteps) {
      setCurrentStep(Math.min(totalSteps, currentStep));
    }
  }, [totalSteps, currentStep]);

  const nextStep = () => {
    setCurrentStep((prev) => Math.min(prev + 1, totalSteps));
  };

  const previousStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const goToStep = (step: number) => {
    if (step >= 1 && step <= totalSteps) {
      // Trigger validation before allowing navigation
      setTriggerStepValidation(true);
      
      // Use a slightly longer delay to ensure validation completes
      setTimeout(() => {
        setTriggerStepValidation(false);
      }, 200);
    }
  };

  // Add a direct step setter without validation
  const setCurrentStepDirect = (step: number) => {
    if (step >= 1 && step <= totalSteps) {
      setCurrentStep(step);
    }
  };

  const setDynamicTotalSteps = (steps: number) => {
    setTotalSteps(steps);
    // Ensure current step is valid for new total
    if (currentStep > steps) {
      setCurrentStep(steps);
    }
  };

  // isLastStep now means the second-to-last step (step before review)
  // In 2-step form (Phase 3), isLastStep refers to step 1
  // In 5-step form (Phase 2), isLastStep refers to step 4
  // In 4-step form (Phase 1), isLastStep refers to step 3
  const isLastStep = totalSteps > 1 ? currentStep === totalSteps - 1 : false;
  
  // isReviewStep is always the final step (review page)
  const isReviewStep = currentStep === totalSteps;

  return (
    <WizardContext.Provider value={{ 
      currentStep, 
      nextStep, 
      previousStep, 
      goToStep,
      totalSteps,
      isLastStep,
      isReviewStep,
      setDynamicTotalSteps,
      triggerStepValidation,
      setTriggerStepValidation,
      setCurrentStepDirect
    }}>
      {children}
    </WizardContext.Provider>
  );
};

export const useWizard = () => {
  const context = useContext(WizardContext);
  if (context === undefined) {
    throw new Error("useWizard must be used within a WizardProvider");
  }
  return context;
};
